
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import { useLocalStorage } from 'react-use';
import { createClient } from '@/utils/supabase/component';
import { useUser } from '@/utils/useUser';
import { validatePassword } from '@/utils/passwordPolicy';
import { useAnalytics } from '@/contexts/Analytics';
import Seo from '@/components/Seo';
import LogosCloudLogin from '@/components/LogosCloudLogin';
import PasswordStrengthHints from '@/components/PasswordStrengthHints';
import TestimonialsSpecial from '@/components/TestimonialsSpecial';
import LastUsedBadge from '@/components/LastUsedBadge';
import Turnstile, { isTurnstileEnabled, TURNSTILE_ERROR_MESSAGE, type TurnstileHandle, } from '@/components/Turnstile';
import { useLastUsedLoginMethod } from '@/hooks/useLastUsedLoginMethod';
const EMAIL_HISTORY_LIMIT = 3;
const normalizeEmail = (value: string) => value.trim().toLowerCase();
// Known inbox providers: powers the "Open <inbox>" shortcut on the
// post-signup confirmation panel and the Google-SSO nudge for Gmail
// addresses (a Gmail user can skip email confirmation entirely).
const INBOX_PROVIDERS: Array<{
    name: string;
    url: string;
    domains: RegExp;
    isGoogle?: boolean;
}> = [
    {
        name: 'Gmail',
        url: 'https://mail.google.com/mail/u/0/',
        domains: /@(gmail\.com|googlemail\.com)$/i,
        isGoogle: true,
    },
    {
        name: 'Outlook',
        url: 'https://outlook.live.com/mail/0/',
        domains: /@(outlook\.|hotmail\.|live\.|msn\.com)/i,
    },
    {
        name: 'Yahoo Mail',
        url: 'https://mail.yahoo.com',
        domains: /@(yahoo\.|ymail\.com)/i,
    },
    {
        name: 'iCloud Mail',
        url: 'https://www.icloud.com/mail',
        domains: /@(icloud\.com|me\.com|mac\.com)$/i,
    },
];
const inboxProviderFor = (address: string | null | undefined) => address
    ? (INBOX_PROVIDERS.find((p) => p.domains.test(address.trim())) ?? null)
    : null;
const buildUniqueEmailHistory = (entries: unknown): string[] => {
    if (!Array.isArray(entries))
        return [];
    return entries.reduce<string[]>((acc, entry) => {
        if (typeof entry !== 'string') {
            return acc;
        }
        const trimmed = entry.trim();
        if (!trimmed) {
            return acc;
        }
        const normalized = normalizeEmail(trimmed);
        const alreadyStored = acc.some((storedEmail) => normalizeEmail(storedEmail) === normalized);
        if (!alreadyStored) {
            acc.push(trimmed);
        }
        return acc;
    }, []);
};
export default function Login() {
    const supabase = createClient();
    const router = useRouter();
    const { user } = useUser();
    const { t, lang } = useLocale('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{
        type: 'error' | 'success';
        text: string;
    } | null>(null);
    const [isSignUp, setIsSignUp] = useState(false);
    // Post-signup confirmation state: when set, the auth form is replaced by a
    // "confirm your email" panel with resend / open-inbox / Google-SSO actions.
    const [awaitingVerification, setAwaitingVerification] = useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resending, setResending] = useState(false);
    // Supabase Auth rejects sign-in/sign-up/resend without a captcha token once
    // Attack Protection is on. The confirmation panel replaces the form, so the
    // resend flow needs its own widget.
    const captchaRef = useRef<TurnstileHandle>(null);
    // Magic-link fallback. Password sign-in can be unavailable for reasons that
    // have nothing to do with the user's credentials (edge rate limiting on the
    // token endpoint, a forgotten password), and the magic-link endpoint is a
    // different route entirely — so this is a genuine second way in.
    const [magicLinkSending, setMagicLinkSending] = useState(false);
    const [magicLinkSent, setMagicLinkSent] = useState(false);
    // Only offered once a password sign-in has actually failed — it is a
    // recovery path, not a primary one. Deliberately NOT offered when the
    // captcha itself failed: the magic-link endpoint is behind the same gate,
    // so the button would be guaranteed to fail too.
    const [showMagicLink, setShowMagicLink] = useState(false);
    const resendCaptchaRef = useRef<TurnstileHandle>(null);
    const [resendMessage, setResendMessage] = useState<{
        type: 'success' | 'error';
        text: string;
    } | null>(null);
    // Set when the Postmark webhook has recorded a hard delivery failure for
    // the address awaiting verification (see /api/verification-delivery-status).
    const [deliveryFailure, setDeliveryFailure] = useState(false);
    // Handle query params
    const { price, avatar, invite_code, preset, showEmail, headshots_join, join, redirectTo, promotionCode, } = router.query;
    const [, setPriceValue] = useLocalStorage('price');
    const [, setPromotionCodeValue] = useLocalStorage('promotionCode');
    const [, setAvatarValue] = useLocalStorage('avatar');
    const [, setInviteCodeValue] = useLocalStorage('invite_code');
    const [, setPresetValue] = useLocalStorage('preset');
    const [, setJoinValue] = useLocalStorage('join');
    const [, setHeadshotsJoinValue] = useLocalStorage('headshots_join');
    const [, setRedirectToValue] = useLocalStorage('redirectTo');
    const [storedEmailHistory, setStoredEmailHistory] = useLocalStorage<string[]>('emailHistory', []);
    const analytics = useAnalytics();
    const { lastUsedLoginMethod, setLastUsedLoginMethod } = useLastUsedLoginMethod();
    // Check for hash fragment on mount (for sign up mode)
    useEffect(() => {
        if (router.asPath.includes('#auth-sign-up')) {
            setIsSignUp(true);
        }
    }, [router.asPath]);
    // Check for error messages in query params
    useEffect(() => {
        const error = router.query.error as string;
        if (error) {
            setMessage({ type: 'error', text: decodeURIComponent(error) });
        }
    }, [router.query.error]);
    // Store query params in localStorage
    useEffect(() => {
        if (price) {
            setPriceValue(price);
        }
    }, [price, setPriceValue]);
    useEffect(() => {
        if (promotionCode)
            setPromotionCodeValue(promotionCode);
    }, [promotionCode, setPromotionCodeValue]);
    useEffect(() => {
        if (avatar)
            setAvatarValue(avatar);
    }, [avatar, setAvatarValue]);
    useEffect(() => {
        if (preset)
            setPresetValue(preset);
    }, [preset, setPresetValue]);
    useEffect(() => {
        if (redirectTo) {
            const target = Array.isArray(redirectTo) ? redirectTo[0] : redirectTo;
            if (target && typeof target === 'string') {
                setRedirectToValue(target);
            }
        }
    }, [redirectTo, setRedirectToValue]);
    useEffect(() => {
        if (invite_code)
            setInviteCodeValue(invite_code);
    }, [invite_code, setInviteCodeValue]);
    useEffect(() => {
        if (join)
            setJoinValue(join);
    }, [join, setJoinValue]);
    useEffect(() => {
        if (headshots_join)
            setHeadshotsJoinValue(headshots_join);
    }, [headshots_join, setHeadshotsJoinValue]);
    // Redirect if user is already logged in
    useEffect(() => {
        if (user) {
            router.push('/dashboard', '/dashboard', { locale: lang });
        }
    }, [user, router, lang]);
    const passwordValidation = validatePassword(password, email);
    const uniqueEmailHistory = buildUniqueEmailHistory(storedEmailHistory);
    const normalizedEmailValue = normalizeEmail(email);
    const hasReachedEmailLimit = uniqueEmailHistory.length >= EMAIL_HISTORY_LIMIT;
    const emailAlreadyTracked = normalizedEmailValue
        ? uniqueEmailHistory.some((entry) => normalizeEmail(entry) === normalizedEmailValue)
        : false;
    const isBlockedByEmailLimit = isSignUp &&
        normalizedEmailValue.length > 0 &&
        hasReachedEmailLimit &&
        !emailAlreadyTracked;
    const remainingEmailSlots = Math.max(EMAIL_HISTORY_LIMIT - uniqueEmailHistory.length, 0);
    const emailLimitMessage = `You've reached the limit of ${EMAIL_HISTORY_LIMIT} accounts on this device. Please continue with an existing email or contact support for assistance.`;
    const emailInputClassName = [
        'mt-1 block w-full rounded-md shadow-sm sm:text-sm dark:bg-gray-700 dark:text-white',
        isBlockedByEmailLimit
            ? 'border-red-400 focus:border-red-500 focus:ring-red-500 dark:border-red-400'
            : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600',
    ].join(' ');
    const submitButtonDisabled = loading ||
        (isSignUp && isBlockedByEmailLimit) ||
        (isSignUp && password.length > 0 && !passwordValidation.isValid);
    useEffect(() => {
        if (message?.type === 'error' && message.text === emailLimitMessage) {
            if (!isBlockedByEmailLimit) {
                setMessage(null);
            }
        }
    }, [message, isBlockedByEmailLimit, emailLimitMessage]);
    // Tick down the resend cooldown once per second.
    useEffect(() => {
        if (resendCooldown <= 0)
            return;
        const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);
    const handleMagicLink = async () => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setMessage({
                type: 'error',
                text: t('Enter your email address first.'),
            });
            return;
        }
        setMagicLinkSending(true);
        setMessage(null);
        try {
            const captchaToken = await captchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setMessage({ type: 'error', text: t(TURNSTILE_ERROR_MESSAGE) });
                return;
            }
            const { error } = await supabase.auth.signInWithOtp({
                email: trimmedEmail,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/confirm?type=magiclink`,
                    // Never create an account from this button — otherwise a typo'd
                    // address silently becomes a second, empty account.
                    shouldCreateUser: false,
                    captchaToken,
                },
            });
            // A missing account errors here, but saying so would let anyone test
            // which emails are registered — so the confirmation is deliberately the
            // same either way.
            if (error && !/signups not allowed|not found/i.test(error.message)) {
                setMessage({ type: 'error', text: error.message });
                return;
            }
            setMagicLinkSent(true);
        }
        catch {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
        }
        finally {
            captchaRef.current?.reset();
            setMagicLinkSending(false);
        }
    };
    const handleResendVerification = async () => {
        if (!awaitingVerification || resending || resendCooldown > 0)
            return;
        setResending(true);
        setResendMessage(null);
        try {
            const captchaToken = await resendCaptchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setResendMessage({ type: 'error', text: t(TURNSTILE_ERROR_MESSAGE) });
                return;
            }
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: awaitingVerification,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/confirm`,
                    captchaToken,
                },
            });
            if (error) {
                setResendMessage({ type: 'error', text: error.message });
            }
            else {
                setResendMessage({
                    type: 'success',
                    text: t('A new confirmation email is on its way.'),
                });
                setResendCooldown(60);
            }
        }
        catch {
            setResendMessage({
                type: 'error',
                text: t('Could not resend the email. Please try again.'),
            });
        }
        finally {
            // Turnstile tokens are single use — clear it whatever the outcome.
            resendCaptchaRef.current?.reset();
            setResending(false);
        }
    };
    // After signup, check (at 20s and 60s) whether the confirmation email hard
    // bounced, so the user sees "couldn't deliver" instead of a silent dead-end.
    useEffect(() => {
        if (!awaitingVerification) {
            setDeliveryFailure(false);
            return;
        }
        let cancelled = false;
        const check = async () => {
            try {
                const res = await fetch(`/api/verification-delivery-status?email=${encodeURIComponent(awaitingVerification)}`);
                const data = await res.json();
                if (!cancelled && data?.failed)
                    setDeliveryFailure(true);
            }
            catch {
                // Best-effort check only.
            }
        };
        const firstCheck = setTimeout(check, 20000);
        const secondCheck = setTimeout(check, 60000);
        return () => {
            cancelled = true;
            clearTimeout(firstCheck);
            clearTimeout(secondCheck);
        };
    }, [awaitingVerification]);
    const handleSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);
        try {
            const captchaToken = await captchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setMessage({ type: 'error', text: t(TURNSTILE_ERROR_MESSAGE) });
                return;
            }
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password,
                options: { captchaToken },
            });
            if (error) {
                setMessage({ type: 'error', text: error.message });
                // Wrong password, or the request never reached Supabase (an edge rate
                // limit returns a CORS-less block page, which surfaces as "Failed to
                // fetch"). A sign-in link is a genuine way around both.
                setShowMagicLink(true);
            }
            else {
                setLastUsedLoginMethod('email');
                // Redirect will happen automatically via auth state change
                window.location.href =
                    lang !== 'en' ? `/${lang}/dashboard/` : '/dashboard/';
            }
        }
        catch (error) {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
            setShowMagicLink(true);
        }
        finally {
            // Turnstile tokens are single use — clear it whatever the outcome.
            captchaRef.current?.reset();
            setLoading(false);
        }
    };
    const handleSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (normalizedEmailValue && hasReachedEmailLimit && !emailAlreadyTracked) {
            setMessage({
                type: 'error',
                text: emailLimitMessage,
            });
            return;
        }
        if (!passwordValidation.isValid) {
            setMessage({
                type: 'error',
                text: passwordValidation.firstError ??
                    'Password does not meet the security requirements.',
            });
            return;
        }
        setLoading(true);
        setMessage(null);
        try {
            // Validate email via API before attempting signup
            const response = await fetch('/api/validate-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });
            const validation = await response.json();
            if (!validation.isValid) {
                setMessage({
                    type: 'error',
                    text: validation.reason || 'Invalid email address',
                });
                setLoading(false);
                return;
            }
            const captchaToken = await captchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setMessage({ type: 'error', text: t(TURNSTILE_ERROR_MESSAGE) });
                return;
            }
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/confirm`,
                    captchaToken,
                },
            });
            if (error) {
                setMessage({ type: 'error', text: error.message });
            }
            else if ((data.user?.identities?.length ?? 0) === 0) {
                // The email already belongs to a CONFIRMED account (e.g. created via
                // Google): Supabase anti-enumeration returns this fake "success" with
                // an identity-less user and sends NO email — and resend() silently
                // no-ops too — so the "Confirm your email" panel would strand the
                // user waiting for mail that will never arrive. Flip to sign-in with
                // their email kept and surface the passwordless rescue, which works
                // whichever provider the account was created with.
                analytics.track('Sign Up Existing Account', { method: 'email' });
                // Mint a fresh captcha token for the courtesy email BEFORE flipping
                // the view — the flip re-renders the widget with the 'login' action
                // and would void the pending token request.
                captchaRef.current?.reset();
                const noticeToken = await captchaRef.current?.getToken();
                setIsSignUp(false);
                setShowMagicLink(true);
                setMessage({
                    type: 'error',
                    text: t('An account with this email already exists. Sign in below — if you originally signed up with Google, use the Google button, or email yourself a sign-in link.'),
                });
                // Best-effort: emails the mailbox owner which provider they signed up
                // with plus a one-click sign-in link. Fire-and-forget by design.
                fetch('/api/notify-existing-account', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, captchaToken: noticeToken }),
                }).catch(() => undefined);
            }
            else {
                // Browser-side twin of the server-fired `Sign Up Success`, which comes
                // from a Supabase DB webhook and therefore carries no session, URL,
                // referrer or UTMs — making landing-page attribution impossible. This
                // one does, so use it as the conversion goal for acquisition tests.
                // Named distinctly so it never double-counts against the server event.
                analytics.track('Sign Up Started', { method: 'email' });
                // Lets the first authenticated load fire `Sign Up Completed` even if
                // the confirmation email is opened hours later (useUser.tsx).
                try {
                    localStorage.setItem('ep_pending_signup', String(Date.now()));
                }
                catch {
                    // storage may be unavailable (private mode) — first-session
                    // detection still covers the common case.
                }
                setStoredEmailHistory((previousValue) => {
                    const existingHistory = buildUniqueEmailHistory(previousValue);
                    if (normalizedEmailValue &&
                        !existingHistory.some((entry) => normalizeEmail(entry) === normalizedEmailValue)) {
                        existingHistory.push(email.trim());
                    }
                    return existingHistory.slice(0, EMAIL_HISTORY_LIMIT);
                });
                // Swap the form for the confirmation panel. The first email just went
                // out, so start the resend cooldown immediately.
                setMessage(null);
                setResendMessage(null);
                setAwaitingVerification(email.trim());
                setResendCooldown(60);
            }
        }
        catch (error) {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
        }
        finally {
            // Turnstile tokens are single use — clear it whatever the outcome.
            captchaRef.current?.reset();
            setLoading(false);
        }
    };
    const handleOAuthSignIn = async (provider: 'google' | 'facebook' | 'apple') => {
        setLoading(true);
        // Remember the chosen provider before we redirect away to the OAuth flow,
        // so the "Last used" hint is ready when the user returns next time.
        setLastUsedLoginMethod(provider);
        try {
            // Determine the correct redirect URL based on environment
            const redirectTo = typeof window !== 'undefined'
                ? `${window.location.origin}/auth/callback`
                : `${process.env.NEXT_PUBLIC_SITE_URL || 'https://easy-peasy.ai'}/auth/callback`;
            const { error } = await supabase.auth.signInWithOAuth({
                provider,
                options: {
                    redirectTo,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    },
                },
            });
            if (error) {
                setMessage({ type: 'error', text: error.message });
            }
        }
        catch (error) {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
        }
        finally {
            setLoading(false);
        }
    };
    return (<div className='flex flex-col items-center justify-center py-8 dark:bg-slate-900 lg:h-screen'>
      <Seo templateTitle={t('Sign In')}/>
      <div className='max-w-8xl m-12 flex h-full flex-col content-center items-center justify-between space-y-8 lg:flex-row lg:justify-around lg:space-x-4 lg:space-y-0'>
        <div className='m-auto flex w-full max-w-lg justify-center lg:pr-8'>
          <div className='w-full'>
            <div>
              <Link href='/' className='' passHref legacyBehavior>
                <span className='flex justify-center text-2xl font-extrabold text-primary-700'>
                  <Image src='/images/marky.png' width='250' height='36' alt='Easy-Peasy.ai logo'/>
                </span>
              </Link>
              <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-slate-200'>
                {t('All-In-One AI Platform')}
              </h2>
              <p className='mt-2 text-center text-sm text-gray-600 dark:text-slate-400'>
                {isSignUp
            ? t("Don't have an account? Sign up")
            : t('Already have an account? Sign in')}
              </p>
            </div>

            {awaitingVerification && (<div className='mt-8'>
                <div className='rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800'>
                  <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30'>
                    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='h-6 w-6 text-primary-600 dark:text-primary-400'>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75'/>
                    </svg>
                  </div>
                  <h3 className='mt-4 text-lg font-semibold text-gray-900 dark:text-slate-200'>
                    {t('Confirm your email')}
                  </h3>
                  <p className='mt-2 text-sm text-gray-600 dark:text-slate-400'>
                    {t('We sent a confirmation link to')}{' '}
                    <span className='font-medium text-gray-900 dark:text-slate-200'>
                      {awaitingVerification}
                    </span>
                  </p>

                  {inboxProviderFor(awaitingVerification)?.isGoogle && (<div className='mt-5 rounded-lg border border-primary-200 bg-primary-50 p-4 text-left dark:border-primary-500/30 dark:bg-primary-500/10'>
                      <p className='text-sm font-semibold text-primary-900 dark:text-primary-100'>
                        {t('Skip the wait — this is a Google account')}
                      </p>
                      <p className='mt-1 text-sm text-primary-800/80 dark:text-primary-100/70'>
                        {t('Continue with Google and you are in instantly — no confirmation email needed.')}
                      </p>
                      <button type='button' onClick={() => handleOAuthSignIn('google')} disabled={loading} className='relative mt-3 flex w-full items-center justify-center rounded-md border border-gray-300 bg-white py-2.5 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'>
                        <svg className='absolute left-4 h-5 w-5' viewBox='0 0 24 24'>
                          <path fill='#4285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/>
                          <path fill='#34A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/>
                          <path fill='#FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/>
                          <path fill='#EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/>
                        </svg>
                        <span>{t('Continue with Google')}</span>
                      </button>
                    </div>)}

                  {inboxProviderFor(awaitingVerification) && (<a href={inboxProviderFor(awaitingVerification)?.url} target='_blank' rel='noopener noreferrer' className='mt-4 flex w-full items-center justify-center rounded-md border border-gray-300 bg-white py-2.5 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'>
                      {t('Open {{provider}}', {
                    provider: inboxProviderFor(awaitingVerification)?.name,
                })}
                    </a>)}

                  {deliveryFailure && (<div className='mt-5 rounded-md bg-red-50 p-3 text-left text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400'>
                      {t("We couldn't deliver the email to this address — it may be invalid. Please use a different email address.")}
                    </div>)}

                  <p className='mt-5 text-xs text-gray-500 dark:text-gray-400'>
                    {t("Didn't get it? Check your spam folder, or:")}
                  </p>
                  <div className='mt-2 flex items-center justify-center gap-3 text-sm'>
                    <button type='button' onClick={handleResendVerification} disabled={resending || resendCooldown > 0} className='font-medium text-primary-600 hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400'>
                      {resendCooldown > 0
                ? t('Resend in {{seconds}}s', {
                    seconds: resendCooldown,
                })
                : resending
                    ? t('Sending...')
                    : t('Resend email')}
                    </button>
                    <span className='text-gray-300 dark:text-gray-600'>·</span>
                    <button type='button' onClick={() => {
                setAwaitingVerification(null);
                setResendMessage(null);
            }} className='font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400'>
                      {t('Use a different email')}
                    </button>
                  </div>

                  <Turnstile ref={resendCaptchaRef} action='resend' className='mt-4 flex justify-center'/>

                  {resendMessage && (<div className={`mt-4 rounded-md p-3 text-sm ${resendMessage.type === 'error'
                    ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    : 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400'}`}>
                      {resendMessage.text}
                    </div>)}
                </div>
              </div>)}

            <div className={awaitingVerification ? 'hidden' : 'mt-8'}>
              <div className='space-y-3'>
                <button type='button' onClick={() => handleOAuthSignIn('google')} disabled={loading} className='relative flex w-full items-center justify-center rounded-md border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'>
                  <svg className='absolute left-4 h-5 w-5' viewBox='0 0 24 24'>
                    <path fill='#4285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/>
                    <path fill='#34A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/>
                    <path fill='#FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/>
                    <path fill='#EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/>
                  </svg>
                  <span>
                    {isSignUp
            ? t('Sign up with {{provider}}', { provider: 'Google' })
            : t('Sign in with {{provider}}', { provider: 'Google' })}
                  </span>
                  {lastUsedLoginMethod === 'google' && (<LastUsedBadge label={t('Last used')}/>)}
                </button>

                <button type='button' onClick={() => handleOAuthSignIn('facebook')} disabled={loading} className='relative flex w-full items-center justify-center rounded-md border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'>
                  <svg className='absolute left-4 h-5 w-5' fill='#1877F2' viewBox='0 0 24 24'>
                    <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z'/>
                  </svg>
                  <span>
                    {isSignUp
            ? t('Sign up with {{provider}}', { provider: 'Facebook' })
            : t('Sign in with {{provider}}', {
                provider: 'Facebook',
            })}
                  </span>
                  {lastUsedLoginMethod === 'facebook' && (<LastUsedBadge label={t('Last used')}/>)}
                </button>

                {/* <button
          type='button'
          onClick={() => handleOAuthSignIn('apple')}
          disabled={loading}
          className='relative flex w-full items-center justify-center rounded-md border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <svg
            className='absolute left-4 h-5 w-5'
            fill='currentColor'
            viewBox='0 0 24 24'
          >
            <path d='M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701' />
          </svg>
          <span>
            {isSignUp
              ? t('Sign up with {{provider}}', { provider: 'Apple' })
              : t('Sign in with {{provider}}', { provider: 'Apple' })}
          </span>
        </button> */}
              </div>

              <div className='mt-6'>
                <div className='relative'>
                  <div className='absolute inset-0 flex items-center'>
                    <div className='w-full border-t border-gray-300 dark:border-gray-600'/>
                  </div>
                  <div className='relative flex justify-center text-sm'>
                    <span className='bg-white px-2 text-gray-500 dark:bg-slate-900 dark:text-gray-400'>
                      {t('Or continue with')}
                    </span>
                  </div>
                </div>
              </div>

              <form onSubmit={isSignUp ? handleSignUp : handleSignIn} className='mt-6 space-y-6'>
                {isSignUp && hasReachedEmailLimit && (<div className='rounded-xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900 shadow-sm dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100'>
                    <div className='flex items-start gap-3'>
                      <div className='mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-400/20 dark:text-amber-100'>
                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' className='h-4 w-4'>
                          <circle cx='12' cy='12' r='9' strokeWidth='1.5'/>
                          <line x1='12' x2='12' y1='8' y2='13' strokeWidth='1.5' strokeLinecap='round'/>
                          <circle cx='12' cy='16' r='1' fill='currentColor'/>
                        </svg>
                      </div>
                      <div className='space-y-2'>
                        <div>
                          <p className='text-sm font-semibold'>
                            {t('Please continue with an existing email')}
                          </p>
                          <p className='mt-1 text-sm text-amber-800/90 dark:text-amber-100/80'>
                            {t('To keep things secure, each browser can create up to {{limit}} accounts.', { limit: EMAIL_HISTORY_LIMIT })}
                          </p>
                        </div>
                        {uniqueEmailHistory.length > 0 && (<div className='flex flex-wrap gap-2 pt-1'>
                            {uniqueEmailHistory.map((historyEmail) => (<span key={normalizeEmail(historyEmail)} className='rounded-full border border-amber-200 bg-white/80 px-3 py-1 text-xs font-medium text-amber-700 shadow-sm dark:border-amber-400/40 dark:bg-transparent dark:text-amber-100/90'>
                                {historyEmail}
                              </span>))}
                          </div>)}
                      </div>
                    </div>
                  </div>)}

                <div>
                  <label htmlFor='email' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                    {t('Email address')}
                  </label>
                  <input id='email' name='email' type='email' autoComplete='email' required value={email} onChange={(e) => setEmail(e.target.value)} className={emailInputClassName} placeholder={t('Your email address')}/>
                  {isSignUp && isBlockedByEmailLimit && (<p className='mt-2 text-sm text-red-600 dark:text-red-400'>
                      {emailLimitMessage}
                    </p>)}
                  {isSignUp &&
            !isBlockedByEmailLimit &&
            inboxProviderFor(email)?.isGoogle && (<p className='mt-2 text-sm text-gray-600 dark:text-slate-400'>
                        {t('Google account detected —')}{' '}
                        <button type='button' onClick={() => handleOAuthSignIn('google')} className='font-medium text-primary-600 underline hover:text-primary-500 dark:text-primary-400'>
                          {t('sign up with Google instead')}
                        </button>{' '}
                        {t('(instant, no confirmation email)')}
                      </p>)}
                </div>

                <div>
                  <label htmlFor='password' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                    {t('Password')}
                  </label>
                  <input id='password' name='password' type='password' autoComplete={isSignUp ? 'new-password' : 'current-password'} required minLength={isSignUp ? 12 : undefined} value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={isSignUp && password.length > 0
            ? !passwordValidation.isValid
            : undefined} aria-describedby={isSignUp ? 'password-requirements' : undefined} className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm' placeholder={t('Your password')}/>
                  {isSignUp && (<div id='password-requirements'>
                      <PasswordStrengthHints rules={passwordValidation.rules}/>
                    </div>)}
                </div>

                {!isSignUp && (<div className='flex items-center justify-between'>
                    <div className='text-sm'>
                      <Link href='/auth/forgot-password' className='font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400'>
                        {t('Forgot your password?')}
                      </Link>
                    </div>
                  </div>)}

                <Turnstile ref={captchaRef} action={isSignUp ? 'signup' : 'login'} className='flex justify-center'/>

                <div>
                  <button type='submit' disabled={submitButtonDisabled} aria-disabled={submitButtonDisabled} title={isSignUp && isBlockedByEmailLimit
            ? emailLimitMessage
            : undefined} className='relative flex w-full justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'>
                    {loading
            ? isSignUp
                ? t('Signing up...')
                : t('Signing in...')
            : isSignUp
                ? t('Sign up')
                : t('Sign in')}
                    {!isSignUp && lastUsedLoginMethod === 'email' && (<LastUsedBadge label={t('Last used')} variant='onPrimary'/>)}
                  </button>
                </div>

                {message && (<div className={`mt-4 p-4 rounded-md ${message.type === 'error'
                ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                : 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400'}`}>
                    {message.text}
                  </div>)}

                {!isSignUp &&
            showMagicLink &&
            (magicLinkSent ? (<div className='rounded-md bg-green-50 p-4 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-400'>
                      {t("If that email has an account, we've sent a sign-in link. Check your inbox.")}
                    </div>) : (<div>
                      <p className='mb-2 text-center text-sm text-gray-600 dark:text-gray-400'>
                        {t("Can't get in? Sign in without your password.")}
                      </p>
                      <button type='button' onClick={handleMagicLink} disabled={magicLinkSending || loading} className='flex w-full justify-center rounded-md border border-gray-300 bg-white py-2.5 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'>
                        {magicLinkSending
                    ? t('Sending...')
                    : t('Email me a sign-in link')}
                      </button>
                    </div>))}
              </form>

              <div className='mt-6 text-center'>
                <button type='button' onClick={() => setIsSignUp(!isSignUp)} className='text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400'>
                  {isSignUp
            ? t('Already have an account? Sign in')
            : t("Don't have an account? Sign up")}
                </button>
              </div>

              <p className='mt-6 text-center text-xs text-gray-500 dark:text-gray-400'>
                {t('By continuing, you agree to the')}{' '}
                <Link href='/terms-of-use' target='_blank' className='text-primary-600 hover:text-primary-500 dark:text-primary-400'>
                  {t('Terms of Service')}
                </Link>{' '}
                {t('and')}{' '}
                <Link href='/privacy-policy' target='_blank' className='text-primary-600 hover:text-primary-500 dark:text-primary-400'>
                  {t('Privacy Policy')}
                </Link>
              </p>

              {/* Trust & Security Badges */}
              <div className='mt-8 border-t border-gray-100 pt-6 dark:border-gray-700'>
                <div className='text-center mb-4'>
                  <p className='text-xs font-medium text-gray-700 dark:text-gray-300'>
                    {t('Enterprise-grade security and compliance')}
                  </p>
                </div>

                <div className='flex items-center justify-center gap-6'>
                  <div className='group flex flex-col items-center space-y-1 transition-all duration-200 hover:scale-105'>
                    <Image src='/images/soc2.png' width={48} height={48} alt='SOC2 Certified' title='SOC2 Type II Certified' className='h-12 w-12 opacity-80 transition-opacity group-hover:opacity-100'/>
                    <span className='text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'>
                      SOC2
                    </span>
                  </div>

                  <div className='group flex flex-col items-center space-y-1 transition-all duration-200 hover:scale-105'>
                    <Image src='/images/iso-27001.png' width={48} height={48} alt='ISO 27001 Certified' title='ISO 27001 Information Security Management' className='h-12 w-12 opacity-80 transition-opacity group-hover:opacity-100'/>
                    <span className='text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'>
                      ISO 27001
                    </span>
                  </div>

                  {/* HIPAA Compliant */}
                  <div className='group flex flex-col items-center space-y-1 transition-all duration-200 hover:scale-105'>
                    <Image src='/images/hipaa.png' width={48} height={48} alt='HIPAA Compliant' title='HIPAA Compliant' className='h-12 w-12 opacity-80 transition-opacity group-hover:opacity-100'/>
                    <span className='text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'>
                      HIPAA
                    </span>
                  </div>

                  <div className='group flex flex-col items-center space-y-1 transition-all duration-200 hover:scale-105'>
                    <Image src='/images/gdpr.png' width={48} height={48} alt='GDPR Compliant' title='General Data Protection Regulation Compliant' className='h-12 w-12 opacity-80 transition-opacity group-hover:opacity-100'/>
                    <span className='text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'>
                      GDPR
                    </span>
                  </div>
                </div>

                <div className='mt-4 text-center'>
                  <Link href='https://easypeasyai.trust.site/' target='_blank' rel='noopener noreferrer' className='inline-flex items-center space-x-1 text-xs text-gray-500 transition-colors hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400'>
                    <svg className='h-3 w-3' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'/>
                    </svg>
                    <span>{t('View our Trust Center')}</span>
                    <svg className='h-3 w-3' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'/>
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='w-full overflow-hidden rounded-2xl bg-primary-100/30 text-center dark:bg-slate-800 lg:w-3/5'>
          <div className='flex flex-col items-center justify-center px-8 pt-12'>
            <div className='space-y-6'>
              <h2 className='text-brand-800 text-2xl md:text-3xl'>
                {t('Join 100,000+ creators, brands & publishers')}
              </h2>
              <LogosCloudLogin />
            </div>
          </div>
          <TestimonialsSpecial />
        </div>
      </div>
    </div>);
}

    async function __Next_Translate__getStaticProps__1a0cbfb65cf__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/login',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb65cf__ as getStaticProps }
  